// LE SUIVI D'UNE FICHE (chantier `fiche-suivi`, 27/10/2026) : le RAIL DE SUIVI
// au bord droit de la fiche et le VOLET qu'il ouvre, dessines d'apres l'atelier
// valide par le fondateur (`docs/maquettes/atelier-2610/README.md`, sections 4
// et 5). Le gabarit DESSINE, il ne lit rien : tout ce qu'il montre arrive par
// la prop `suivi` de `ScreenLayout` (`SuiviDuGabarit`, `fiche-contrat.ts`),
// construite par le runtime depuis la conversation `chatter` de la fiche et le
// journal des changements (`docs/socle/fiche-suivi.md`, section 1).
//
// LA PATE EST CELLE DE LIA (`packages/shell/src/centre/ConversationLia.tsx`) :
// bulles `taki-bulle`, jours `taki-jour-du-fil`, barre `BarreDeSaisie`, cartes
// sur `accent-tint`, fil dans l'ordre d'une conversation (le plus recent en
// bas, juste au-dessus de la saisie). Ce qu'on ECRIT est a droite, en vert ; ce
// qui se PASSE est a gauche, au nom de la personne.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, JSX } from 'react';
import { cn } from '../lib/cn.js';
import { useEveil } from '../lib/useEveil.js';
import { Avatar } from './Avatar.js';
import { usePersonnesPort } from '../lib/personnes.js';
import { BarreDeSaisie } from '../conversations/BarreDeSaisie.js';
import { initialesDe } from '../conversations/ChampAvecMentions.js';
import { heureDe, libelleDeJour } from '../conversations/FilDeConversation.js';
import { LiaAuTravail } from '../conversations/LiaAuTravail.js';
import { entreesDuComposeur, MENTION_INCONNUE, type EntreeDeMention } from '../conversations/mentions.js';
import { TexteDuMessage } from '../conversations/TexteDuMessage.js';
import { useSourceDeMentions } from '../conversations/useSourceDeMentions.js';
import {
  IconArrowRight,
  IconBellSetTimer,
  IconCheck,
  IconMailSendEnvelope,
  IconNewFile,
  IconPen1,
  IconPin1,
} from '../icons/index.js';
import type {
  ActiviteDuSuivi,
  DetailDuCompte,
  EntreeDuSuivi,
  PersonneDuSuivi,
  SuiviDuGabarit,
} from './fiche-contrat.js';

/* -------------------------------------------------------------------------- */
/* La logique pure (tenue par `tests/suivi-gabarit.test.mjs`, sans DOM)        */
/* -------------------------------------------------------------------------- */

/** Un jour du fil : son separateur, puis ses entrees, de la plus ancienne a la plus recente. */
export interface JourDuSuivi {
  /** `YYYY-MM-DD` local : la clef React du jour. */
  cle: string;
  /** « Aujourd'hui », « Hier », « Vendredi 23 octobre » (`libelleDeJour` du fil des conversations). */
  libelle: string;
  entrees: EntreeDuSuivi[];
}

function cleDuJour(date: Date): string {
  const mois = String(date.getMonth() + 1).padStart(2, '0');
  const jour = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${mois}-${jour}`;
}

/**
 * LE FIL REGROUPE PAR JOUR, dans l'ordre d'une conversation. Le contrat promet
 * deja les entrees de la plus ancienne a la plus recente ; on retrie quand
 * meme, d'un tri STABLE sur l'instant : un message envoye (`enAttente`) et un
 * evenement serveur arrives dans le desordre ne doivent jamais remonter le fil.
 */
export function joursDuSuivi(entrees: readonly EntreeDuSuivi[], maintenant: Date = new Date()): JourDuSuivi[] {
  const triees = entrees
    .map((entree, rang) => ({ entree, rang }))
    .sort((a, b) => a.entree.at.getTime() - b.entree.at.getTime() || a.rang - b.rang)
    .map(({ entree }) => entree);
  const jours: JourDuSuivi[] = [];
  for (const entree of triees) {
    const cle = cleDuJour(entree.at);
    const dernier = jours[jours.length - 1];
    if (dernier !== undefined && dernier.cle === cle) dernier.entrees.push(entree);
    else jours.push({ cle, libelle: libelleDeJour(entree.at, maintenant), entrees: [entree] });
  }
  return jours;
}

/**
 * LE COTE D'UNE ENTREE : un MESSAGE, quel qu'en soit l'auteur, a droite (la
 * bulle verte) ; un changement ou un evenement, a gauche (la ligne d'outil de
 * Lia, ouverte par la bulle de profil). Atelier, section 4.
 */
export function coteDeLEntree(entree: EntreeDuSuivi): 'droite' | 'gauche' {
  return entree.sorte === 'message' ? 'droite' : 'gauche';
}

/**
 * LES BULLES DU RAIL : chaque personne UNE fois, a sa contribution la plus
 * recente, la plus recente en tete. Le runtime les donne deja dans cet ordre ;
 * on le garantit ici, parce que c'est l'ordre que l'oeil lit (« qui vient de
 * passer ») et qu'une bulle en double se lirait comme deux personnes.
 */
export function ordreDesContributeurs<P extends PersonneDuSuivi & { derniere: Date }>(contributeurs: readonly P[]): P[] {
  const parPersonne = new Map<string, P>();
  for (const personne of contributeurs) {
    const vue = parPersonne.get(personne.id);
    if (vue === undefined || personne.derniere.getTime() > vue.derniere.getTime()) parPersonne.set(personne.id, personne);
  }
  return [...parPersonne.values()].sort((a, b) => b.derniere.getTime() - a.derniere.getTime());
}

/** Les sortes qu'une bulle de la decomposition peut isoler dans le volet. */
export type SorteDuFiltre = keyof DetailDuCompte;

/** LE FILTRE DU VOLET : une sorte, une personne, les deux (ET), ou rien. */
export interface FiltreDuSuivi {
  sorte?: SorteDuFiltre | undefined;
  /** L'`id` d'une personne : l'auteur d'un message, le `par` d'un changement ou d'un evenement. */
  personne?: string | undefined;
}

const SORTE_DE_L_ENTREE: Readonly<Record<SorteDuFiltre, EntreeDuSuivi['sorte']>> = {
  messages: 'message',
  changements: 'changement',
  evenements: 'evenement',
};

/** Vrai quand le filtre ne retient rien : le fil est montre tel quel. */
export function filtreVide(filtre: FiltreDuSuivi): boolean {
  return filtre.sorte === undefined && filtre.personne === undefined;
}

/**
 * LE FIL FILTRE : ne garde que la sorte demandee et/ou que les entrees de la
 * personne demandee (les deux a la fois : ET). Sans filtre, c'est le fil tel
 * quel (meme tableau). L'ordre recu est conserve.
 */
export function filtrerLeSuivi(entrees: readonly EntreeDuSuivi[], filtre: FiltreDuSuivi): readonly EntreeDuSuivi[] {
  if (filtreVide(filtre)) return entrees;
  return entrees.filter((entree) => {
    if (filtre.sorte !== undefined && entree.sorte !== SORTE_DE_L_ENTREE[filtre.sorte]) return false;
    if (filtre.personne !== undefined) {
      const qui = entree.sorte === 'message' ? entree.auteur : entree.par;
      if (qui?.id !== filtre.personne) return false;
    }
    return true;
  });
}

/** Une ligne de la decomposition du total, au survol du rail. */
export interface LigneDuDetail {
  sorte: keyof DetailDuCompte;
  nombre: number;
  /** « 1 message », « 3 changements » : l'infobulle et le lecteur d'ecran. */
  libelle: string;
}

const MOTS_DU_DETAIL: Readonly<Record<keyof DetailDuCompte, readonly [string, string]>> = {
  messages: ['message', 'messages'],
  changements: ['changement', 'changements'],
  evenements: ['événement', 'événements'],
};

/** Les lignes de la decomposition, dans l'ordre de l'atelier, sans les sortes a zero. */
export function lignesDuDetail(detail: DetailDuCompte): LigneDuDetail[] {
  return (Object.keys(MOTS_DU_DETAIL) as (keyof DetailDuCompte)[])
    .filter((sorte) => detail[sorte] > 0)
    .map((sorte) => {
      const nombre = detail[sorte];
      const [un, plusieurs] = MOTS_DU_DETAIL[sorte];
      return { sorte, nombre, libelle: `${nombre} ${nombre > 1 ? plusieurs : un}` };
    });
}

/**
 * L'ECHEANCE D'UNE ACTIVITE, une date PURE `YYYY-MM-DD` lue en jour local
 * (jamais un `new Date('YYYY-MM-DD')`, qui la lirait a minuit UTC et la
 * decalerait d'un jour a l'ouest de Greenwich). « Demain » en plus des mots du
 * fil : une activite regarde devant elle.
 */
export function libelleDEcheance(echeance: string, maintenant: Date = new Date()): string {
  const morceaux = /^(\d{4})-(\d{2})-(\d{2})$/.exec(echeance);
  if (morceaux === null) return echeance;
  const date = new Date(Number(morceaux[1]), Number(morceaux[2]) - 1, Number(morceaux[3]));
  const demain = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate() + 1);
  return cleDuJour(date) === cleDuJour(demain) ? 'Demain' : libelleDeJour(date, maintenant);
}

/** L'infobulle d'une bulle du rail : « Celia Martin, à 10:42 », ou « ..., le mardi 20 octobre ». */
export function quandDeContribution(derniere: Date, maintenant: Date = new Date()): string {
  return cleDuJour(derniere) === cleDuJour(maintenant)
    ? `à ${heureDe(derniere)}`
    : `le ${libelleDeJour(derniere, maintenant).toLocaleLowerCase('fr')}`;
}

/** La taille d'une piece jointe, comme la carte Fichiers de la fiche : « 248 Ko ». */
export function tailleDeFichier(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  const [valeur, unite] = octets < 1048576 ? [octets / 1024, 'Ko'] : [octets / 1048576, 'Mo'];
  return `${valeur.toLocaleString('fr-LU', { maximumFractionDigits: 1 })} ${unite}`;
}

/* -------------------------------------------------------------------------- */
/* Les briques, a la maniere de Lia                                            */
/* -------------------------------------------------------------------------- */

/* Le texte des bulles, a la taille de la fenetre compacte de Lia. */
const TEXTE = 'text-[0.8125rem] leading-5';

/* LA PASTILLE d'une valeur, celle de la fiche suivie par Lia. */
const PASTILLE =
  'inline-flex h-[1.125rem] max-w-full items-center truncate rounded-pill border border-accent-tint-border bg-accent-tint px-1.5 text-[0.6875rem] font-semibold text-accent-ink';

/* LA CARTE sur `accent-tint`, celle des fiches que Lia cite. */
const CARTE =
  'flex min-h-9 max-w-full items-center gap-2 rounded-sm border border-accent-tint-border bg-accent-tint px-2 py-1';
const TUILE_DE_CARTE =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded-xs bg-surface text-accent-soft-ink';

/* UN BOUTON TEXTE du volet (« Fait », « Renvoyer », « Retirer »). */
const BOUTON_TEXTE =
  'h-7 shrink-0 cursor-pointer rounded-sm px-2 text-xs font-semibold text-ink-600 hover:text-ink-900 focus-visible:outline-none focus-visible:shadow-ring';

/* L'ELEMENT SELECTIONNE DU RAIL (retour du fondateur, 29/10) : au repos rien,
   au survol le fond `nav-hover` du dock, l'encadre blanc SEULEMENT sur le
   filtre actif. Recliquer dessus revient a tout. */
const ENCADRE_ACTIF = 'bg-surface text-ink-900 ring-1 ring-inset ring-border-strong';

/* LE VOLET, en rem : sa largeur s'anime, son contenu garde la sienne. */
const LARGEUR_DU_VOLET = 'w-[24rem]';

/** LA BULLE DE PROFIL d'une personne : sa photo, ou ses initiales (`Avatar`, jamais une icone). */
function BulleDeProfil({
  personne,
  title,
  className,
}: {
  personne: PersonneDuSuivi | null;
  /** `null` : aucune infobulle (le bouton qui l'enveloppe porte la sienne). */
  title?: string | null;
  className?: string;
}): JSX.Element {
  const nom = personne?.nom ?? MENTION_INCONNUE;
  return (
    <Avatar
      size={24}
      initials={personne == null ? '?' : initialesDe(nom)}
      src={personne?.photo}
      {...(title === null ? {} : { title: title ?? nom })}
      {...(className === undefined ? {} : { className })}
    />
  );
}

/* UN MESSAGE, quel qu'en soit l'auteur : la bulle VERTE de Lia, TOUT ALIGNE A
   DROITE, l'auteur et l'heure au-dessus. Un message pas encore confirme par la
   base s'attenue et dit « Envoi… » a cote de l'heure : il est deja la ou il
   restera. Un envoi refuse (`echec`) garde sa couleur, prend un anneau
   d'erreur et dit pourquoi dessous, avec « Renvoyer » et « Retirer ». L'article
   porte `data-envoi` (`en-cours`, `echec`) ; un message confirme n'en a pas.
   LE TEXTE EST CELUI DES CONVERSATIONS (`TexteDuMessage`) : les mentions
   stockees (`@[personId]`) s'y lisent en noms et ouvrent le direct, une
   reponse de Lia garde son rendu. Un identifiant ne s'affiche jamais. */
function Bulle({
  entree,
  mentions,
  moi,
  renvoyer,
  abandonner,
}: {
  entree: Extract<EntreeDuSuivi, { sorte: 'message' }>;
  mentions: readonly EntreeDeMention[];
  moi: string | null;
  renvoyer?: ((entreeId: string) => void) | undefined;
  abandonner?: ((entreeId: string) => void) | undefined;
}): JSX.Element {
  const enEchec = entree.echec !== undefined;
  const enAttente = entree.enAttente === true && !enEchec;
  const envoi = enEchec ? 'echec' : enAttente ? 'en-cours' : undefined;
  return (
    <article
      {...(envoi === undefined ? {} : { 'data-envoi': envoi })}
      className={cn('mt-4 flex justify-end', enAttente && 'opacity-60')}
    >
      <div className="flex min-w-0 max-w-[min(86%,calc(100%-2.5rem))] flex-col items-end">
        <div className="mb-1.5 flex w-full flex-wrap items-baseline justify-end gap-x-2 gap-y-0.5 text-xs">
          <span className="font-semibold text-ink-900">{entree.auteur.nom}</span>
          {enAttente && <span className="text-[0.6875rem] text-ink-400">Envoi…</span>}
          <time className="text-[0.6875rem] text-ink-500" dateTime={entree.at.toISOString()}>
            {heureDe(entree.at)}
          </time>
        </div>
        <div
          className={cn(
            'taki-bulle taki-bulle-moi taki-bulle-pointe max-w-full whitespace-pre-wrap break-words',
            enEchec && 'ring-1 ring-danger',
            TEXTE,
          )}
        >
          <TexteDuMessage texte={entree.texte} auteur={entree.auteur.id} entrees={mentions} moi={moi} />
        </div>
        {enEchec && (
          <p role="alert" className="mt-1.5 flex flex-wrap items-center justify-end gap-x-1 text-xs leading-4 text-danger">
            <span>Non envoyé : {entree.echec}</span>
            {renvoyer != null && (
              <button type="button" onClick={() => renvoyer(entree.id)} className={BOUTON_TEXTE}>
                Renvoyer
              </button>
            )}
            {abandonner != null && (
              <button type="button" onClick={() => abandonner(entree.id)} className={BOUTON_TEXTE}>
                Retirer
              </button>
            )}
          </p>
        )}
      </div>
    </article>
  );
}

/* LE RESTE (changements, evenements) : A GAUCHE, pas de bulle de message, la
   ligne d'outil de Lia au texte attenue, ouverte par la BULLE DE PROFIL de qui
   a fait le geste. Un changement liste chaque champ : l'ancienne valeur
   estompee, une fleche, la nouvelle en pastille. */
function Trace({
  entree,
}: {
  entree: Extract<EntreeDuSuivi, { sorte: 'changement' | 'evenement' }>;
}): JSX.Element {
  const changements = entree.sorte === 'changement' ? entree.changements : [];
  const texte =
    entree.sorte === 'evenement' ? entree.texte : changements.length > 0 ? 'a modifié' : (entree.texte ?? 'a modifié');
  return (
    <div className="mt-4 flex items-start gap-2 text-xs leading-4 text-ink-500">
      <BulleDeProfil personne={entree.par} />
      <div className="min-w-0 pt-1">
        <p>
          <span className="font-semibold text-ink-700">{entree.par?.nom ?? MENTION_INCONNUE}</span> {texte} ·{' '}
          <time dateTime={entree.at.toISOString()}>{heureDe(entree.at)}</time>
        </p>
        {changements.length > 0 && (
          <ul className="mt-1.5 flex flex-col gap-1">
            {changements.map((changement, rang) => (
              <li key={`${changement.libelle}-${rang}`} className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                <span className="text-ink-500">{changement.libelle}</span>
                <span className={changement.avant == null ? 'italic text-ink-300' : 'text-ink-400'}>
                  {changement.avant ?? 'vide'}
                </span>
                <IconArrowRight size={10} className="shrink-0 text-ink-300" />
                {changement.apres == null ? (
                  <span className="italic text-ink-300">vide</span>
                ) : (
                  <span className={PASTILLE}>{changement.apres}</span>
                )}
              </li>
            ))}
          </ul>
        )}
        {entree.sorte === 'evenement' && entree.fichier != null && (
          <span className={cn(CARTE, 'mt-2')}>
            <span aria-hidden className={TUILE_DE_CARTE}>
              <IconNewFile size={13} />
            </span>
            <span className="min-w-0 truncate text-[0.8125rem] font-semibold leading-5 text-ink-900">
              {entree.fichier.nom}
            </span>
            {entree.fichier.taille != null && (
              <span className="shrink-0 text-[0.6875rem] leading-4 text-ink-500">
                {tailleDeFichier(entree.fichier.taille)}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

/* UNE ACTIVITE PREVUE, epinglee sous l'en-tete : ce qui reste a faire passe
   avant ce qui a ete fait. Une carte de Lia ; « Fait » la barre. Sans droit
   d'ecrire (`lectureSeule`, ou rien pour l'ecrire), le lien ne se rend pas :
   la carte se lit. */
function Activite({
  activite,
  onBasculer,
}: {
  activite: ActiviteDuSuivi;
  onBasculer: (() => void) | null;
}): JSX.Element {
  const quand = [
    activite.echeance == null ? null : libelleDEcheance(activite.echeance),
    activite.assigne?.nom ?? null,
  ].filter((morceau): morceau is string => morceau != null);
  return (
    <div className={CARTE}>
      <span aria-hidden className={TUILE_DE_CARTE}>
        <IconBellSetTimer size={13} />
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-[0.8125rem] font-semibold leading-5',
            activite.faite ? 'text-ink-400 line-through' : 'text-ink-900',
          )}
        >
          {activite.libelle}
        </span>
        {quand.length > 0 && <span className="block text-[0.6875rem] leading-4 text-ink-500">{quand.join(' · ')}</span>}
      </span>
      {onBasculer != null && (
        <button
          type="button"
          onClick={onBasculer}
          className="shrink-0 text-xs font-semibold text-ink-600 underline-offset-2 hover:underline focus-visible:shadow-ring"
        >
          {activite.faite ? 'Rouvrir' : 'Fait'}
        </button>
      )}
    </div>
  );
}

/* LE SQUELETTE DU FIL, tant que la premiere page n'est pas arrivee : la forme
   d'une trace a gauche et d'une bulle a droite, jamais un faux contenu. */
function SqueletteDuFil(): JSX.Element {
  return (
    <div aria-hidden className="flex flex-col gap-5 pt-6">
      {[0, 1, 2].map((rang) => (
        <div key={rang} className="flex flex-col gap-5">
          <span className="flex items-center gap-2">
            <span className="taki-skeleton block h-6 w-6 shrink-0 rounded-full" />
            <span className="taki-skeleton block h-2.5 w-40 rounded-xs" />
          </span>
          <span className="taki-skeleton block h-9 w-48 self-end rounded-lg" />
        </div>
      ))}
    </div>
  );
}

/** La raison d'un envoi refuse, en mots : celle que le runtime a rejetee. */
function raisonDuRefus(erreur: unknown): string {
  if (erreur instanceof Error && erreur.message.trim() !== '') return erreur.message;
  if (typeof erreur === 'string' && erreur.trim() !== '') return erreur;
  return "Le message n'est pas parti.";
}

/* LA SAISIE, celle de Lia et des conversations (`BarreDeSaisie`) : un message,
   rien d'autre. Entree envoie, Maj + Entree saute une ligne. `@` propose ce
   que propose une conversation (`useSourceDeMentions`, `entreesDuComposeur`) :
   les personnes de l'annuaire, `@tous` et Lia ; le texte envoye porte les
   jetons (`@[personId]`), d'ou le SDK derive `mentions` comme pour tout
   message. Aucun second mecanisme. Le texte part tout de suite, la barre se
   vide et reste libre : on peut envoyer plusieurs messages d'affilee (le
   runtime montre chacun en attente). Le texte ne revient dans la barre, avec
   la raison dessous, que si la promesse REJETTE (le message n'a pas pu etre
   pose du tout, cas socle absent) ; un echec d'ecriture reste dans le fil. */
function Saisie({
  envoyer,
  mentions,
}: {
  envoyer: (texte: string) => Promise<void>;
  mentions: readonly EntreeDeMention[];
}): JSX.Element {
  const [texte, setTexte] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const partir = (): void => {
    const message = texte.trim();
    if (message === '') return;
    setRefus(null);
    setTexte('');
    envoyer(message).then(
      () => undefined,
      (erreur: unknown) => {
        setTexte((courant) => (courant === '' ? message : courant));
        setRefus(raisonDuRefus(erreur));
      },
    );
  };
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        partir();
      }}
      className="shrink-0 bg-surface px-2.5 pb-2.5 pt-1"
    >
      {refus != null && (
        <p role="alert" className="mb-1.5 px-1 text-xs leading-4 text-ink-700">
          {refus}
        </p>
      )}
      <BarreDeSaisie
        value={texte}
        onChange={setTexte}
        entrees={mentions}
        onEnvoyer={partir}
        peutEnvoyer={texte.trim() !== ''}
        placeholder="Écrire un message…"
        ariaLabel="Message"
        lignesMax={8}
      />
    </form>
  );
}

/* La distance au haut du fil (en px de defilement, une mesure du navigateur et
   non un style) a partir de laquelle on demande la page plus ancienne. */
const SEUIL_DES_PLUS_ANCIENNES = 48;


/* L'EPINGLE DU SUIVI dans la fenetre Conversations : un bouton texte a droite
   de « Suivi ». Inerte pendant le geste ; si le geste est refuse, la raison
   remonte (la ligne d'alerte est sous l'en-tete). */
function BoutonEpingle({
  epingle,
  onRefus,
}: {
  epingle: NonNullable<SuiviDuGabarit['epingle']>;
  onRefus: (raison: string | null) => void;
}): JSX.Element {
  const [enGeste, setEnGeste] = useState(false);
  return (
    <button
      type="button"
      aria-pressed={epingle.epinglee}
      aria-disabled={enGeste || undefined}
      onClick={() => {
        if (enGeste) return;
        setEnGeste(true);
        onRefus(null);
        epingle.basculer().then(
          () => setEnGeste(false),
          (erreur: unknown) => {
            setEnGeste(false);
            onRefus(raisonDuRefus(erreur));
          },
        );
      }}
      className={cn(
        'inline-flex h-7 shrink-0 items-center gap-1 rounded-sm px-2 text-xs font-semibold text-ink-600 hover:text-ink-900 focus-visible:outline-none focus-visible:shadow-ring',
        enGeste ? 'cursor-default opacity-60' : 'cursor-pointer',
      )}
    >
      <IconPin1 size={12} />
      {epingle.epinglee ? 'Désépingler du chatbot' : 'Épingler dans le chatbot'}
    </button>
  );
}

/**
 * LE CONTENU DU VOLET : l'en-tete « Suivi » (et l'epingle du chatbot), la ligne
 * du filtre actif, les activites epinglees, le fil qui defile, la saisie en
 * bas. Le fil s'ouvre CALE EN BAS, sur le plus recent, et y revient a chaque
 * entree nouvelle (la derniere entree FILTREE) ; une page plus ancienne arrivee
 * par le haut ne le fait pas sauter (le defilement est compense de la hauteur
 * ajoutee).
 *
 * LE FILTRE (une sorte, une personne) vient du rail : il se pose en cliquant
 * une bulle de la decomposition ou une tete, se retire ici. Les activites
 * epinglees ne sont ni un message ni un changement : elles se taisent des qu'un
 * filtre est actif.
 */
function ContenuDuVolet({
  suivi,
  ouvert,
  filtre,
  onFiltre,
}: {
  suivi: SuiviDuGabarit;
  ouvert: boolean;
  filtre: FiltreDuSuivi;
  onFiltre: (suite: FiltreDuSuivi) => void;
}): JSX.Element {
  const entrees = suivi.entrees ?? [];
  const visibles = filtrerLeSuivi(entrees, filtre);
  const zone = useRef<HTMLDivElement | null>(null);
  const premiereBrute = entrees[0]?.id;
  const premiere = visibles[0]?.id;
  /* LA DERNIERE ENTREE ET SON ETAT D'ENVOI : un refus ajoute la ligne « Non
     envoyé » sous la bulle deja calee, le fil se recale pour la montrer. */
  const derniereEntree = visibles[visibles.length - 1];
  const derniere = derniereEntree === undefined ? undefined
    : derniereEntree.sorte === 'message' ? `${derniereEntree.id}|${derniereEntree.enAttente === true}|${derniereEntree.echec ?? ''}` : derniereEntree.id;
  const cleDuFiltre = `${filtre.sorte ?? ''}|${filtre.personne ?? ''}`;
  /* LIA AU TRAVAIL : sa bulle est en bas du fil, la ou sa reponse arrivera ; quand elle change, le fil redescend. */
  const lia = suivi.lia;
  const signatureLia = lia?.signature;
  const memoire = useRef<{
    premiere: string | undefined;
    derniere: string | undefined;
    hauteur: number;
    ouvert: boolean;
    filtre: string;
    lia: string | undefined;
  }>({
    premiere: undefined,
    derniere: undefined,
    hauteur: 0,
    ouvert: false,
    filtre: '',
    lia: undefined,
  });
  /* La page plus ancienne deja demandee, par la premiere entree (non filtree)
     d'alors : une seule demande par page, meme si le defilement repasse sous
     le seuil. */
  const demandee = useRef<string | undefined | null>(null);
  const demanderPlusAnciennes = (): void => {
    if (suivi.plusAnciennes == null || suivi.chargement === true || demandee.current === premiereBrute) return;
    demandee.current = premiereBrute;
    suivi.plusAnciennes();
  };

  useLayoutEffect(() => {
    const el = zone.current;
    if (el == null) return;
    const avant = memoire.current;
    if (ouvert) {
      if (!avant.ouvert || avant.derniere !== derniere || avant.filtre !== cleDuFiltre || avant.lia !== signatureLia) el.scrollTop = el.scrollHeight;
      else if (avant.premiere !== premiere) el.scrollTop += el.scrollHeight - avant.hauteur;
      // UN FIL PLUS COURT QUE LE VOLET ne defile pas, donc ne pourrait jamais
      // demander la suite : on la demande des qu'il reste de la place.
      if (el.scrollHeight <= el.clientHeight) demanderPlusAnciennes();
    }
    memoire.current = { premiere, derniere, hauteur: el.scrollHeight, ouvert, filtre: cleDuFiltre, lia: signatureLia };
  });

  /* UNE SECONDE FOIS apres l'ouverture : la largeur du volet s'anime, la
     hauteur du fil bouge encore pendant l'animation, et le dernier message
     restait coupe. */
  useEffect(() => {
    if (!ouvert) return;
    const minuterie = window.setTimeout(() => {
      const el = zone.current;
      if (el != null) el.scrollTop = el.scrollHeight;
    }, 400);
    return () => window.clearTimeout(minuterie);
  }, [ouvert]);

  /* LES MENTIONS : la source des conversations, celle d'un suivi (`chatter`). */
  const source = useSourceDeMentions();
  const mentions = entreesDuComposeur(source.entrees, 'chatter');
  const moi = usePersonnesPort()?.moi?.personId ?? null;
  const jours = joursDuSuivi(visibles);
  const filtreActif = !filtreVide(filtre);
  const activites = filtreActif ? [] : (suivi.activites ?? []);
  const marquerFaite = suivi.lectureSeule === true ? undefined : suivi.marquerFaite;
  const peutEcrire = suivi.lectureSeule !== true && suivi.envoyer != null;
  const [refusEpingle, setRefusEpingle] = useState<string | null>(null);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface">
      <header className="flex h-10 shrink-0 items-center justify-between gap-2 border-b border-border pl-3 pr-1.5 text-[0.8125rem] font-semibold text-ink-900">
        Suivi
        {suivi.epingle != null && <BoutonEpingle epingle={suivi.epingle} onRefus={setRefusEpingle} />}
      </header>
      {refusEpingle != null && (
        <p role="alert" className="shrink-0 px-3 pt-2 text-xs leading-4 text-danger">
          {refusEpingle}
        </p>
      )}
      {activites.length > 0 && (
        <div className="flex shrink-0 flex-col gap-1.5 px-3 pt-3">
          {activites.map((activite) => (
            <Activite
              key={activite.id}
              activite={activite}
              onBasculer={
                marquerFaite == null
                  ? null
                  : () => {
                      void marquerFaite(activite.id, !activite.faite).catch(() => undefined);
                    }
              }
            />
          ))}
        </div>
      )}
      <div
        ref={zone}
        onScroll={(event) => {
          if (event.currentTarget.scrollTop < SEUIL_DES_PLUS_ANCIENNES) demanderPlusAnciennes();
        }}
        className="taki-scroll-mask taki-scroll-mask-top min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-3"
      >
        {suivi.chargement === true && entrees.length === 0 ? (
          <SqueletteDuFil />
        ) : jours.length === 0 ? (
          <p className="pt-6 text-center text-xs text-ink-400">Aucune entrée</p>
        ) : (
          jours.map((jour) => (
            <section key={jour.cle}>
              <div role="separator" className="taki-jour-du-fil">
                {jour.libelle}
              </div>
              {jour.entrees.map((entree) =>
                entree.sorte === 'message' ? (
                  <Bulle
                    key={entree.id}
                    entree={entree}
                    mentions={mentions}
                    moi={moi}
                    renvoyer={suivi.renvoyer}
                    abandonner={suivi.abandonner}
                  />
                ) : (
                  <Trace key={entree.id} entree={entree} />
                ),
              )}
            </section>
          ))
        )}
        {lia?.attentes.map((attente) => (
          <LiaAuTravail
            key={`lia-${attente.messageId}`}
            etape={attente.etape}
            depuis={attente.depuis}
            echec={attente.echec}
            maintenant={lia.maintenant}
            compact
            className="mt-4 gap-x-2.5 py-0"
          />
        ))}
      </div>
      {peutEcrire && suivi.envoyer != null && <Saisie envoyer={suivi.envoyer} mentions={mentions} />}
    </div>
  );
}

/* LES ICONES de la decomposition, une par sorte, grises : aucune couleur. */
const ICONE_DU_DETAIL: Readonly<Record<keyof DetailDuCompte, typeof IconPen1>> = {
  messages: IconMailSendEnvelope,
  changements: IconPen1,
  evenements: IconCheck,
};

/**
 * LE COMPTEUR QUI SE DECOMPOSE. Au repos, le cercle blanc et le total. Au
 * survol du rail (ou au focus clavier), et TOUT LE TEMPS tant que le volet est
 * ouvert, le total SE DECOMPOSE VERS LE BAS en BULLES a la hauteur du cercle :
 * une icone grise et un nombre par sorte d'entree. Chaque bulle part de sous le
 * total, glisse jusqu'a sa place et apparait en fondu, l'une apres l'autre ; les
 * bulles de profil en dessous descendent d'autant. A la sortie, tout remonte
 * ensemble.
 *
 * CHAQUE BULLE EST UN BOUTON QUI FILTRE LE VOLET (`aria-pressed` quand elle est
 * la sorte active) : un clic ne montre que cette sorte, un second retire le
 * filtre ; volet ferme, le clic l'ouvre ainsi filtre.
 *
 * LA HAUTEUR S'ANIME PAR LA GRILLE (`grid-rows` de 0fr a 1fr) : la seule
 * facon d'animer vers une hauteur `auto` sans la mesurer. La piste n'est PAS
 * rognee : au depart, les bulles sont posees sous le total, invisibles, et
 * c'est de la qu'elles descendent. Le retard ne joue qu'a l'OUVERTURE.
 */
function CompteurDecompose({
  total,
  lignes,
  ouvert,
  sorteActive,
  onSorte,
}: {
  total: number | null;
  lignes: readonly LigneDuDetail[];
  ouvert: boolean;
  sorteActive: SorteDuFiltre | undefined;
  onSorte: (sorte: SorteDuFiltre) => void;
}): JSX.Element {
  return (
    <span className="flex flex-col items-center">
      <span className="relative z-10 flex h-6 w-6 items-center justify-center rounded-pill text-[0.6875rem] font-semibold tabular-nums text-ink-900">
        {total ?? ''}
      </span>
      <span
        style={{ transitionTimingFunction: 'var(--ease-standard)' }}
        className={cn(
          'grid transition-[grid-template-rows] duration-300 motion-reduce:transition-none',
          ouvert
            ? 'grid-rows-[1fr]'
            : 'grid-rows-[0fr] group-hover/rail:grid-rows-[1fr] group-has-[:focus-visible]/rail:grid-rows-[1fr]',
        )}
      >
        <span className="min-h-0">
          <span className="flex flex-col items-center gap-0.5 pt-1">
            {lignes.map(({ sorte, nombre, libelle }, rang) => {
              const Icone = ICONE_DU_DETAIL[sorte];
              const active = sorteActive === sorte;
              return (
                <span
                  key={sorte}
                  style={
                    {
                      '--depart': `calc(${rang + 1} * -1.625rem)`,
                      '--retard': `${40 + rang * 50}ms`,
                      transitionTimingFunction: 'var(--ease-standard)',
                    } as CSSProperties
                  }
                  className={cn(
                    'transition duration-300 motion-reduce:transition-none',
                    ouvert
                      ? 'pointer-events-auto opacity-100 [transform:none] [transition-delay:var(--retard)]'
                      : [
                          'pointer-events-none opacity-0 [transform:translateY(var(--depart))] [transition-delay:0ms]',
                          'group-hover/rail:pointer-events-auto group-hover/rail:opacity-100 group-hover/rail:[transform:none] group-hover/rail:[transition-delay:var(--retard)]',
                          'group-has-[:focus-visible]/rail:pointer-events-auto group-has-[:focus-visible]/rail:opacity-100 group-has-[:focus-visible]/rail:[transform:none]',
                        ],
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSorte(sorte)}
                    aria-pressed={active}
                    aria-label={`Ne montrer que les ${MOTS_DU_DETAIL[sorte][1]} (${nombre})`}
                    title={libelle}
                    className={cn(
                      'flex h-6 cursor-pointer items-center gap-1 rounded-pill px-1.5 text-[0.6875rem] font-semibold tabular-nums transition-colors focus-visible:outline-none focus-visible:shadow-ring motion-reduce:transition-none',
                      active ? ENCADRE_ACTIF : 'text-ink-700 hover:bg-nav-hover',
                    )}
                  >
                    <Icone size={11} className={active ? 'text-ink-700' : 'text-ink-400'} />
                    {nombre}
                  </button>
                </span>
              );
            })}
          </span>
        </span>
      </span>
    </span>
  );
}

/**
 * LE RAIL DE SUIVI ET SON VOLET, poses par `ScreenLayout` a droite de la zone 5
 * d'une FICHE quand la prop `suivi` est fournie.
 *
 * LE RAIL (3rem, pleine hauteur, bord droit) est le PENDANT DU DOCK sur l'autre
 * bord : meme fond `nav-dock`, meme filet `nav-border`, meme fondu au repos
 * (`useEveil`). Il porte le nombre d'entrees du fil (`compte`, un `count()`
 * serveur), sa decomposition, puis les bulles des contributeurs. LE FOND DU
 * RAIL EST UN BOUTON DE BASCULE (un bouton ne contient pas d'autre bouton) :
 * un clic dessus, cercle du total compris, ouvre le volet, un second le ferme,
 * comme Echap. Par-dessus, seules les bulles de la decomposition et les tetes
 * des personnes sont cliquables : elles FILTRENT le volet (et l'ouvrent s'il
 * est ferme). Le filtre s'efface quand le volet se ferme.
 *
 * LE VOLET (24rem) EST LE PROLONGEMENT DU RAIL : il s'ouvre vers la gauche,
 * colle a lui, fond blanc sans cadre, et la FICHE RECULE pour lui laisser la
 * place. C'est sa LARGEUR qui s'anime ; son contenu garde la sienne.
 *
 * ETEINT (`raison`, G16) : le rail reste a sa place, ne s'ouvre pas et dit
 * pourquoi. `aria-disabled`, jamais `disabled` : un bouton `disabled` ne
 * recoit pas la souris, son `title` ne se lirait jamais.
 */
export function SuiviDeFiche({ suivi }: { suivi: SuiviDuGabarit }): JSX.Element {
  const eteint = suivi.raison != null;
  const [ouvertDemande, setOuvert] = useState(false);
  const ouvert = ouvertDemande && !eteint;
  const eveil = useEveil(ouvert);
  const [filtre, setFiltre] = useState<FiltreDuSuivi>({});
  /* Le filtre s'efface quand le volet se ferme (Echap, bascule, eteint). */
  useEffect(() => {
    if (!ouvert) setFiltre((courant) => (filtreVide(courant) ? courant : {}));
  }, [ouvert]);
  /* Un clic sur une bulle ou une tete : pose le filtre, ou le retire si c'est
     celui qui est deja actif ; volet ferme, il s'ouvre ainsi filtre. */
  const filtrerParSorte = (sorte: SorteDuFiltre): void => {
    setFiltre((courant) => ({ ...courant, sorte: courant.sorte === sorte ? undefined : sorte }));
    setOuvert(true);
  };
  const filtrerParPersonne = (id: string): void => {
    setFiltre((courant) => ({ ...courant, personne: courant.personne === id ? undefined : id }));
    setOuvert(true);
  };

  /* LA DECOMPOSITION, lue a la demande : au premier survol (ou focus) ou a
     l'ouverture du volet, puis de nouveau seulement quand le total a change
     depuis la derniere lecture (meme volet ouvert, sans survol), sinon la somme
     des lignes mentirait sur le cercle. Un echec se tait (le cercle garde son
     total) et se retente a la lecture suivante. */
  const [detail, setDetail] = useState<DetailDuCompte | null>(null);
  const lu = useRef<{ pour: number | null } | null>(null);
  const lireLeDetail = (): void => {
    const lire = suivi.detail;
    if (lire == null || eteint || (lu.current !== null && lu.current.pour === suivi.compte)) return;
    const pour = suivi.compte;
    lu.current = { pour };
    lire().then(setDetail, () => {
      if (lu.current?.pour === pour) lu.current = null;
    });
  };
  useEffect(() => {
    if (ouvert) lireLeDetail();
    // `lireLeDetail` ne depend que de `suivi` et d'une ref : on relit sur ces deux-la.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, suivi.compte]);
  const lignes = detail == null || eteint ? [] : lignesDuDetail(detail);
  const contributeurs = eteint ? [] : ordreDesContributeurs(suivi.contributeurs ?? []);

  /* LE CHAT DE LIA S'ECARTE quand le volet est ouvert (decision du 27/10,
     `docs/socle/fiche-suivi.md`) : sinon il couvre la saisie du suivi. Le
     drapeau sur <html> est lu par `tokens.css`, qui range le lanceur au coin
     bas droit de la FICHE, a gauche du volet. */
  useEffect(() => {
    if (!ouvert) return;
    const racine = document.documentElement;
    racine.setAttribute('data-suivi-ouvert', '');
    return () => racine.removeAttribute('data-suivi-ouvert');
  }, [ouvert]);

  /* ECHAP FERME LE VOLET, ET RIEN D'AUTRE. Ecoute en CAPTURE, pour passer
     avant `useScreen`, qui ferme la FICHE sur Echap sauf touche deja traitee
     (`defaultPrevented`) : sans ca, une frappe ferait les deux. */
  useEffect(() => {
    if (!ouvert) return;
    const auClavier = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      setOuvert(false);
    };
    window.addEventListener('keydown', auClavier, true);
    return () => window.removeEventListener('keydown', auClavier, true);
  }, [ouvert]);

  const titre = eteint
    ? `${suivi.label} : ${suivi.raison}`
    : ouvert
      ? 'Fermer le suivi'
      : suivi.compte == null
        ? suivi.label
        : `${suivi.label} : ${suivi.compte} ${suivi.compte > 1 ? 'éléments' : 'élément'}`;
  /* L'INFOBULLE DONNE LE DETAIL EN TOUTES LETTRES, une fois lu. */
  const infobulle = lignes.length > 0 && !ouvert ? `${titre} (${lignes.map((l) => l.libelle).join(', ')})` : titre;

  return (
    <>
      <div
        aria-hidden={!ouvert}
        inert={!ouvert}
        style={{ transitionDuration: 'var(--dur-slow)', transitionTimingFunction: 'var(--ease-standard)' }}
        className={cn(
          'relative flex shrink-0 overflow-hidden bg-surface transition-[width] motion-reduce:transition-none',
          ouvert ? LARGEUR_DU_VOLET : 'w-0',
        )}
      >
        {/* Le filet, sur le bord gauche du volet, entre la fiche et lui. */}
        <span aria-hidden className="absolute inset-y-0 left-0 z-10 w-px bg-nav-border" />
        {/* `data-taki-hors-ecran` : un Cmd+Z tape dans la saisie n'annule pas
            une modification de la fiche. */}
        <aside aria-label="Suivi" data-taki-hors-ecran className={cn('flex h-full shrink-0 flex-col', LARGEUR_DU_VOLET)}>
          <ContenuDuVolet suivi={suivi} ouvert={ouvert} filtre={filtre} onFiltre={setFiltre} />
        </aside>
      </div>
      <div className="flex w-12 shrink-0 flex-col">
        <div
          {...eveil.gestes}
          onMouseEnter={() => {
            eveil.gestes.onMouseEnter();
            lireLeDetail();
          }}
          className="group/rail relative flex flex-1 flex-col bg-nav-dock"
        >
          {/* LA BASCULE couvre tout le rail, en arriere-plan : un clic sur le
              fond (le cercle du total compris) tombe sur elle. */}
          <button
            type="button"
            onFocus={lireLeDetail}
            aria-expanded={eteint ? undefined : ouvert}
            aria-disabled={eteint || undefined}
            aria-label={titre}
            title={infobulle}
            data-testid="rail-de-suivi"
            onClick={eteint ? undefined : () => setOuvert((courant) => !courant)}
            className={cn(
              'absolute inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-border',
              eteint ? 'cursor-default' : 'cursor-pointer',
            )}
          />
          {/* Le filet du dock, dessine par-dessus le bord : une bordure
              decalerait le contenu d'un pixel. Il reste volet ouvert : il
              separe le blanc du volet et le gris du rail. */}
          <span aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-px bg-nav-border" />
          {/* Le contenu laisse passer la souris vers la bascule ; seules les
              bulles cliquables la reprennent. Eteint, aucune ne se rend. */}
          <span
            className={cn(
              'pointer-events-none relative flex flex-col items-center gap-3 pt-3',
              eteint ? 'opacity-50' : eveil.fondu,
            )}
          >
            <CompteurDecompose
              total={eteint ? null : suivi.compte}
              lignes={lignes}
              ouvert={ouvert}
              sorteActive={filtre.sorte}
              onSorte={filtrerParSorte}
            />
            {contributeurs.length > 0 && (
              /* LES CONTRIBUTEURS, le DERNIER en haut. DEUX NIVEAUX seulement :
                 le dernier en plein, tous les autres au meme niveau estompe,
                 jamais un degrade de crans. Chaque tete est un bouton qui
                 filtre le volet sur cette personne. */
              <span className="flex flex-col items-center gap-1">
                {contributeurs.map((personne, rang) => {
                  const active = filtre.personne === personne.id;
                  const quand = `${personne.nom}, ${quandDeContribution(personne.derniere)}`;
                  return (
                    <button
                      key={personne.id}
                      type="button"
                      onClick={() => filtrerParPersonne(personne.id)}
                      aria-pressed={active}
                      title={`${quand}. Ne montrer que ${personne.nom}`}
                      aria-label={`Ne montrer que ${personne.nom}`}
                      className={cn(
                        'pointer-events-auto flex cursor-pointer rounded-full p-0.5 transition-colors focus-visible:outline-none focus-visible:shadow-ring motion-reduce:transition-none',
                        active ? ENCADRE_ACTIF : 'hover:bg-nav-hover',
                      )}
                    >
                      <BulleDeProfil
                        personne={personne}
                        title={null}
                        className={cn('transition-opacity duration-300', rang === 0 ? 'opacity-100' : 'opacity-50')}
                      />
                    </button>
                  );
                })}
              </span>
            )}
          </span>
        </div>
      </div>
    </>
  );
}
